import { Color, ColorV2 } from "config/colors";
import { FontFamily, FontSize, FontSizeV2, TypographyV2 } from "config/font";
import styled from "styled-components";

type FontProps = {
  color?: Color | ColorV2;
  fontSize?: FontSize;
  backgroundColor?: Color;
  fontFamily?: FontFamily;
  typography?: keyof typeof TypographyV2;
  padding?: boolean;
  align?: "center" | "left" | "right";
  marginLeft?: number;
  alignSelf?:
    | "auto"
    | "flex-start"
    | "flex-end"
    | "center"
    | "baseline"
    | "stretch";
};

export const LabelStyled = styled.span<{
  color?: Color;
  fontFamily?: FontFamily;
  fontSize?: FontSize;
}>`
  color: ${(props) => (props.color ? props.color : Color.SeaBlue600)};
  ${TypographyV2.b1}
`;

export const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: ${ColorV2.Blue7};
  border-radius: 14px;
  border: none;
  box-shadow: none;
  padding: 12px;
  width: 100%;
  max-width: 640px;
  justify-self: start;

  @media (max-width: 1024px) {
    max-width: 100%;
  }

  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 16px;
  }

  @media (max-width: 1180px) and (orientation: portrait) {
    height: 100%;
  }
`;

export const FormStyled = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (max-width: 1180px) and (orientation: portrait) {
    height: 100%;
    min-height: 0;
  }
`;

export const TextInput = styled.input<FontProps>`
  width: 100%;
  padding: 0 20px;
  ${TypographyV2.h3}
  color: ${Color.White};
  background-color: ${ColorV2.Blue1};
  border-radius: 20px;
  height: clamp(70px, 9.8vh, 92px);
  border: none;
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: ${Color.White};
  }
`;

export const CompanyNameContainer = styled.div`
  text-align: right;
  margin-top: 100px;
  width: 100%;
  p {
    margin-right: 100px;
  }
  @media (max-width: 912px) {
    p {
      text-align: center;
      font-size: 18px;
    }
  }
`;

export const InputContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  margin-bottom: 10px;

  @media (max-width: 1180px) and (orientation: portrait) {
    margin-bottom: 8px;
  }
`;

export const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${ColorV2.Blue1};
  border-radius: 0;
  box-sizing: border-box;
  padding: 24px;
  width: 100%;
  height: auto;
  min-height: 0;
  align-self: stretch;
  align-items: center;
  justify-content: center;
  text-align: center;

  @media (max-width: 1180px) and (orientation: portrait) {
    order: 1;
    height: auto;
    padding: 72px 24px 112px;
  }

  @media (max-width: 900px) {
    order: 1;
    height: auto;
    padding: 72px 24px 112px;
  }
`;

export const ClockStampLayout = styled.div`
  width: 100%;
  background-color: ${ColorV2.Blue7};
  display: grid;
  grid-template-columns: minmax(520px, 1.1fr) minmax(360px, 0.9fr);
  gap: 0;
  padding: 0 0 0 20px;
  box-sizing: border-box;
  align-items: stretch;
  flex: 1;

  @media (max-width: 1180px) and (orientation: portrait) {
    grid-template-columns: 1fr;
    gap: 0;
    max-width: none;
    margin: 0;
    padding: 0;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 0;
    max-width: none;
    margin: 0;
    padding: 0;
  }

  @media (max-width: 1024px) and (orientation: portrait) {
    padding: 0;
  }
`;

export const ClockStampInfoTitle = styled.p<FontProps>`
  color: ${ColorV2.White};
  font-size: clamp(24px, 3vw, 40px);
  font-family: ${FontFamily.SpaceGroteskMedium}, Helvetica;
  font-weight: 500;
  line-height: 1.1;
  text-align: center;
  margin: 0 0 12px;
`;

export const ClockStampInfoDate = styled.p<FontProps>`
  color: ${ColorV2.White};
  font-size: clamp(24px, 3vw, 40px);
  font-family: ${FontFamily.SpaceGroteskMedium}, Helvetica;
  font-weight: 500;
  line-height: 1.1;
  text-align: center;
  margin: 0;
`;

export const ClockStampInfoTime = styled.p<FontProps>`
  color: ${ColorV2.White};
  font-size: clamp(68px, 7.2vw, 96px);
  font-family: ${FontFamily.SpaceGroteskBold}, Helvetica;
  font-weight: 700;
  line-height: 1.02;
  text-align: center;
  margin: 14px 0 22px;
`;

export const ClockStampFormArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: 100%;
  padding-top: 0;
  padding-right: 18px;
  padding-left: 8px;
  padding-bottom: 12px;
  margin-top: 0;

  @media (max-width: 1180px) and (orientation: portrait) {
    order: 2;
    align-items: stretch;
    justify-content: flex-start;
    min-height: 0;
    padding-top: 0;
    padding-right: 8px;
    padding-left: 8px;
    margin-top: -56px;
    position: relative;
    z-index: 2;
    overflow: hidden;
  }

  @media (max-width: 900px) {
    order: 2;
    align-items: stretch;
    justify-content: flex-start;
    min-height: 0;
    padding-top: 0;
    padding-right: 8px;
    padding-left: 8px;
    margin-top: -56px;
    position: relative;
    z-index: 2;
    overflow: hidden;
  }
`;

export const Text = styled.p<FontProps>`
  color: ${(props) => (props.color ? props.color : Color.MidnightBlue)};
  ${(props) => TypographyV2[props.typography || "b2"]}
  text-decoration: none;
  background-color: ${(props) =>
    props.backgroundColor ? props.backgroundColor : "none"};
  ${(props) => props.padding && "padding: 5px; border-radius: 4px;"}
  ${(props) => props.align && `text-align: ${props.align};`}
  align-self: ${(props) => (props.alignSelf ? props.alignSelf : "none")};
  margin-left: ${(props) => (props.marginLeft ? `${props.marginLeft}px` : 0)};
`;

export const ModuleInactiveContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
`;

export const ModuleInactiveIconContainer = styled.div`
  margin-top: 150px;
`;

export const CheckInOutModal = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 370px;
  background-color: ${Color.White};
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  outline: none;
  border-radius: 8px;
  button {
    font-size: ${FontSizeV2.Small};
  }
  p {
    margin: 0 0 30px 0;
  }
`;

export const ClockStampCreateAutoGeneratedGigBody = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 500px;
  background-color: ${Color.White};
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  outline: none;
  border-radius: 8px;
  button {
    font-size: ${FontSizeV2.Small};
  }
  p {
    margin: 0 0 30px 0;
  }
  @media (max-width: 480px) {
    width: 370px;
  }
`;

export const ButtonContainer = styled.div`
  display: grid;
  grid-template: 1fr / 1fr 1fr;
  width: fit-content;
  margin: auto;
  margin-top: 20px;

  button {
    margin: 0 10px;
    width: 150px;
    height: 50px;
    font-size: ${FontSizeV2.Standard};
  }
`;

export const CheckInChooseTimeModal = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 600px;
  height: 400px;
  background-color: ${Color.White};
  padding: 40px;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  outline: none;
  border-radius: 8px;
  p {
    margin: 0 0 30px 0;
  }
  button {
    height: 100px;
    font-size: ${FontSizeV2.Standard};
  }
  @media (max-width: 480px) {
    width: 370px;
  }
`;

export const GigInfoContainer = styled.div`
  background-color: ${ColorV2.Blue7};
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const TimeReportModal = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 500px;
  max-height: 90vh;
  max-width: 90vw;
  background-color: ${Color.White};
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  outline: none;
  border-radius: 8px;
  button {
    font-size: ${FontSizeV2.Small};
  }
  p {
    margin: 0 0 30px 0;
  }
  p,
  label,
  input,
  textarea {
    color: ${ColorV2.Blue1} !important;
  }

  input::placeholder,
  textarea::placeholder {
    color: ${ColorV2.Blue1} !important;
    opacity: 0.7;
  }

  overflow-y: scroll;
  overflow-x: scroll;

  @media (max-width: 480px) {
    width: 370px;
  }
`;

export const Row = styled.div`
  background-color: ${Color.White};
  margin: 10px 0;
  padding: 10px;
  display: grid;
  align-items: center;
  gap: 10px 20px;
  grid-template-columns: 1fr auto auto;

  border-bottom: 1px solid ${Color.SeaBlue200};

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  p {
    margin: 0;
  }
`;

export const ClockStampModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;

  p {
    margin: 0;
  }
`;

export const Container1 = styled.div`
  display: flex;
  flex-direction: row;
`;

export const Container2 = styled.div`
  display: flex;
  flex-direction: row;
`;

export const Container3 = styled.div`
  display: flex;
  flex-direction: row;

  @media (max-width: 640px) {
    justify-content: flex-start;
  }
`;
